import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: toYmd(from), to: toYmd(to) };
}

// Dates arrive as 'YYYY-MM-DD' strings; parse by parts so the browser's time
// zone can't shift the day.
function fmtDate(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtTime(t) {
  if (!t) return '—';
  const [h, m] = String(t).split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return '—';
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
}

const STATUS_TONE = {
  pending: 'bg-amber-100 text-amber-700',
  suspending: 'bg-orange-100 text-orange-700',
  suspended: 'bg-orange-100 text-orange-700',
  inactivating: 'bg-red-100 text-red-700',
  inactivated: 'bg-red-100 text-red-700',
  postponed: 'bg-gray-100 text-gray-700',
};

const COLUMNS = [
  { key: 'studentId', label: 'Student ID' },
  { key: 'studentName', label: 'Name' },
  { key: 'registrationNumber', label: 'Reg. No.' },
  { key: 'registrationStatus', label: 'Reg. Status' },
  { key: 'batch', label: 'Batch' },
  { key: 'course', label: 'Course' },
  { key: 'presentDate', label: 'Present Date' },
  { key: 'subject', label: 'Subject' },
  { key: 'startTime', label: 'Start' },
  { key: 'endTime', label: 'End' },
  { key: 'lecturer', label: 'Lecturer' },
  { key: 'hall', label: 'Hall' },
];

export default function NonActivePresentReport() {
  const [range, setRange] = useState(defaultRange);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axiosInstance.get('/admin/coordinator-reports/courses')
      .then((r) => setCourses(r.data?.courses || []))
      .catch(() => {});
  }, []);

  const rangeError = !range.from || !range.to
    ? 'Choose both a from and a to date.'
    : range.from > range.to ? 'The from date cannot be after the to date.' : '';

  useEffect(() => {
    if (rangeError) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    axiosInstance.get('/admin/coordinator-reports/non-active-present', {
      params: { from: range.from, to: range.to, ...(courseId ? { courseId } : {}) },
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err?.response?.data?.message || 'Failed to load the report.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range.from, range.to, courseId, rangeError]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return all;
    return all.filter((r) => {
      const haystack = `${r.studentId} ${r.studentName} ${r.registrationNumber} ${r.batch} ${r.subject} ${r.lecturer} ${r.hall}`.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [data, search]);

  // Exports exactly what is on screen: the chosen date range and course, plus
  // any search filter. IDs are trimmed (a few are stored with a trailing space)
  // and dates kept as YYYY-MM-DD text so they sort correctly in Excel.
  const handleDownload = useCallback(() => {
    if (!rows.length) return;
    const header = [
      'Student ID', 'Name', 'Registration Number', 'Registration Status', 'Batch', 'Course',
      'Present Date', 'Subject', 'Start Time', 'End Time', 'Lecturer', 'Hall',
    ];
    const body = rows.map((r) => [
      String(r.studentId || '').trim(),
      r.studentName,
      String(r.registrationNumber || '').trim(),
      r.registrationStatus,
      r.batch,
      r.course,
      r.presentDate,
      r.subject,
      r.startTime ? fmtTime(r.startTime) : '',
      r.endTime ? fmtTime(r.endTime) : '',
      r.lecturer,
      r.hall,
    ]);
    const aoa = [header, ...body];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = header.map((_, col) => ({
      wch: Math.min(48, Math.max(10, ...aoa.map((row) => String(row[col] ?? '').length)) + 2),
    }));
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: header.length - 1 } }) };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Present - Not Active');

    const courseName = courses.find((c) => String(c.courseId) === String(courseId))?.shortName
      || courses.find((c) => String(c.courseId) === String(courseId))?.fullName || '';
    const suffix = courseName ? `_${courseName.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '')}` : '';
    XLSX.writeFile(wb, `present_students_not_active${suffix}_${range.from}_to_${range.to}.xlsx`);
  }, [rows, courses, courseId, range.from, range.to]);

  const inputClass = 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';

  return (
    <DashboardLayout title="Present Students – Not Active">
      <div className="min-h-screen space-y-5 bg-gray-50 px-6 py-8">
        <div className="border-b border-gray-200 pb-5">
          <h1 className="text-xl font-bold tracking-tight text-gray-900">Present Students – Registration Not Active</h1>
          <p className="mt-1 text-sm text-gray-500">
            Students marked present in a class whose registration status is not currently active
            (inactivated, suspended, pending, and so on).
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <label className="text-sm font-medium text-gray-700">
            <span className="mb-1 block text-xs text-gray-500">From</span>
            <input type="date" className={inputClass} value={range.from} max={range.to || undefined}
              onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))} />
          </label>
          <label className="text-sm font-medium text-gray-700">
            <span className="mb-1 block text-xs text-gray-500">To</span>
            <input type="date" className={inputClass} value={range.to} min={range.from || undefined}
              onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))} />
          </label>
          <label className="min-w-[16rem] flex-1 text-sm font-medium text-gray-700 sm:max-w-md">
            <span className="mb-1 block text-xs text-gray-500">Course</span>
            <select className={`${inputClass} w-full`} value={courseId} onChange={(e) => setCourseId(e.target.value)}>
              <option value="">{courses.length > 1 ? 'All my courses' : 'All courses'}</option>
              {courses.map((c) => <option key={c.courseId} value={c.courseId}>{c.fullName}</option>)}
            </select>
          </label>
          <label className="min-w-[14rem] flex-1 text-sm font-medium text-gray-700 sm:max-w-xs">
            <span className="mb-1 block text-xs text-gray-500">Search</span>
            <input type="search" className={`${inputClass} w-full`} value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, ID, reg. no, batch, subject…" />
          </label>
        </div>

        {rangeError && <p className="text-sm text-red-600">{rangeError}</p>}
        {error && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        {data?.truncated && (
          <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Showing the most recent {data.total.toLocaleString()} records. Narrow the date range or pick a course to see the rest.
          </p>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="text-sm font-semibold text-gray-800">
              {loading ? 'Loading…' : `${rows.length.toLocaleString()} record${rows.length === 1 ? '' : 's'}`}
              {!loading && search && data ? <span className="font-normal text-gray-400"> of {data.total.toLocaleString()}</span> : null}
            </span>
            <div className="flex items-center gap-3">
              {!loading && data && (
                <span className="text-xs text-gray-400">{fmtDate(data.from)} – {fmtDate(data.to)}</span>
              )}
              <button
                type="button"
                onClick={handleDownload}
                disabled={loading || rows.length === 0}
                title={rows.length === 0 ? 'Nothing to download' : 'Download the records shown as an Excel file'}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
                </svg>
                Download Excel
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className="whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr><td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-gray-400">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-gray-400">
                      {search
                        ? 'No records match your search.'
                        : 'No students with a non-active registration were marked present in this period.'}
                    </td>
                  </tr>
                ) : rows.map((r) => (
                  <tr key={r.attendanceId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-indigo-600">{r.studentId}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{r.studentName}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{r.registrationNumber}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${STATUS_TONE[r.registrationStatus] || 'bg-gray-100 text-gray-700'}`}>
                        {r.registrationStatus || 'unknown'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{r.batch}</td>
                    <td className="px-3 py-2 text-gray-700">{r.course}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{fmtDate(r.presentDate)}</td>
                    <td className="px-3 py-2 text-gray-700">{r.subject}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtTime(r.startTime)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtTime(r.endTime)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{r.lecturer || '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700">{r.hall || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
